﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import {
    type SelfServiceLoginErrorState,
    SelfServiceLoginRegistrationPage,
    type SelfServiceLoginRequestData,
    type SelfServiceLoginViewState,
} from "./Types";
import type { IntlContext } from "~base/Internationalization/IntlContext";
import {
    type SetResourceMapLoaded,
    type SetResourcesLoaded,
} from "~base/Pages/Home/Main/Store";
import type { PostSelfServiceLoginRegistrationResponse } from "~jrad/Services/API/Account/PostSelfServiceLoginRegistration";
import type { GetSelfServiceLoginInfoResponse } from "~jrad/Services/API/Account/GetSelfServiceLoginInfo";
import log from "loglevel";

//-------------------------------------------------------------------------------------
// State
//-------------------------------------------------------------------------------------

type SetFetchError = { type: "fetchError"; message: string };
type SetCurrentPage = {
    type: "setCurrentPage";
    page: SelfServiceLoginRegistrationPage;
};
type SetRequestData = {
    type: "setRequestData";
    key: keyof Omit<SelfServiceLoginRequestData, "hasAcceptedToS">;
    value: string;
};
type SetRequestDataBool = {
    type: "setRequestData";
    key: keyof Pick<SelfServiceLoginRequestData, "hasAcceptedToS">;
    value: boolean;
};
type SetResponseData = {
    type: "setResponseData";
    data: PostSelfServiceLoginRegistrationResponse;
};
type SetLoginInfo = {
    type: "setLoginInfo";
    data: GetSelfServiceLoginInfoResponse;
};
type WaitForTextCodesToLoad = { type: "waitForTextCodesToLoad" };

export type SelfServiceLoginViewAction =
    | SetFetchError
    | SetCurrentPage
    //| SetRequestData
    | SetRequestDataBool
    | SetLoginInfo
    | SetResponseData
    | WaitForTextCodesToLoad
    | SetResourcesLoaded
    | SetResourceMapLoaded;

export function reducer(
    state: SelfServiceLoginViewState,
    action: SelfServiceLoginViewAction,
    intl: IntlContext
): SelfServiceLoginViewState {
    switch (action.type) {
        case "fetchError": {
            return {
                ...state,
                errorMessage: action.message,
            };
        }
        case "waitForTextCodesToLoad": {
            return {
                ...state,
                waitForTextCodesToLoad: true,
            };
        }
        case "setResourceMapLoaded": {
            return {
                ...state,
            };
        }
        case "setResourcesLoaded": {
            return {
                ...state,
                waitForTextCodesToLoad: false,
            };
        }
        case "setCurrentPage": {
            return {
                ...state,
                page: action.page,
                nextPageAfterLoading: action.page,
                errorMessage: "",
            };
        }
        case "setLoginInfo": {
            return {
                ...state,
                loginInfo: action.data,
            };
        }
        case "setRequestData": {
            return {
                ...state,
                requestData: {
                    ...state.requestData,
                    [action.key]: action.value,
                },
            };
        }
        case "setResponseData": {
            if (action.data.HasError === true) {
                const errorState: SelfServiceLoginErrorState = {};
                let errorMessage = "";

                action.data.Errors.forEach((error) => {
                    if (error.Field == "generic") {
                        if (errorMessage.length > 0) {
                            errorMessage += "\n";
                        }

                        errorMessage += intl.formatText(
                            error.Message,
                            ...error.MessageArguments
                        );
                    } else {
                        errorState[error.Field] = intl.formatText(
                            error.Message,
                            ...error.MessageArguments
                        );
                    }
                });

                return {
                    ...state,
                    errorMessage: errorMessage,
                    responseHasError: true,
                    responseError: errorState,
                };
            } else {
                let hasStoredAuthToken = false;

                if (action.data.LoginToken) {
                    const token = action.data.LoginToken;

                    log.debug(`Storing new API token`);
                    token.absolute_expiration = new Date();
                    token.absolute_expiration.setSeconds(
                        token.absolute_expiration.getSeconds() +
                            token.expires_in
                    );
                    sessionStorage.setItem("apiToken", JSON.stringify(token));

                    hasStoredAuthToken = true;
                }

                return {
                    ...state,
                    errorMessage: "",
                    responseError: {},
                    responseHasError: false,
                    responseData: action.data,
                    nextPageAfterLoading: state.page,
                    hasStoredAuthToken,
                };
            }
        }
    }
}

// ----------------------------------------------------------------------------------------------------
// Initial state
// ----------------------------------------------------------------------------------------------------

export const initialState: SelfServiceLoginViewState = {
    page: SelfServiceLoginRegistrationPage.TermsOfService,
    nextPageAfterLoading: SelfServiceLoginRegistrationPage.TermsOfService,

    requestData: {
        hasAcceptedToS: false,
    },
    loginInfo: null,
    responseHasError: false,
    responseData: {
        LoginToken: null,
    },
    hasStoredAuthToken: false,
    responseError: {},
};
